// JOURNEY LAB — the Operator's oversight surface for scenario catalogue, scripted replay, isolated live
// control, and durable run evidence. The component is render-first: focused hooks own every read, mutation
// and polling rule; child components render typed safe projections and wire explicit human controls.

import { useMemo } from 'react';
import { ScreenBody, ScreenToolbar, TbInfo, TbStamp } from '../app/screen-chrome.tsx';
import { JourneyCatalogue } from './components/JourneyCatalogue.tsx';
import { JourneyLiveControl } from './components/JourneyLiveControl.tsx';
import { JourneyRunDetail } from './components/JourneyRunDetail.tsx';
import { httpJourneyLabApi, type JourneyLabApi } from './journey-lab-api.ts';
import { journeyModePresentation } from './journey-language.ts';
import { httpJourneyLiveApi, type JourneyLiveApi } from './journey-live-api.ts';
import { Panel, StatusPill } from './primitives.tsx';
import { useJourneyLab, type JourneyLabController } from './useJourneyLab.ts';
import { useJourneyLive } from './useJourneyLive.ts';

export interface JourneyLabSurfaceProps {
  /** A hermetic typed transport for UI tests; omitted in the app to use authenticated same-origin HTTP. */
  readonly api?: JourneyLabApi;
  /** The separately fenced live-control transport; it never carries provider credentials or payloads. */
  readonly liveApi?: JourneyLiveApi;
}

const SCRIPTED_PRESENTATION = journeyModePresentation('scripted');

function ScriptedRailPanel(): React.JSX.Element {
  return (
    <Panel className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-text">Scripted checks available</h2>
          <p className="mt-1 max-w-3xl text-sm text-text-muted">
            {SCRIPTED_PRESENTATION.proofBoundary}
          </p>
        </div>
        <StatusPill tone="go">{SCRIPTED_PRESENTATION.label}</StatusPill>
      </div>
    </Panel>
  );
}

function CataloguePane({
  lab,
  live,
}: {
  readonly lab: JourneyLabController;
  readonly live: ReturnType<typeof useJourneyLive>;
}): React.JSX.Element {
  if (lab.catalogueError) {
    return (
      <Panel className="p-5">
        <p className="text-sm text-text-muted">The scenario catalogue is unavailable.</p>
        <p className="mt-1 text-sm text-text-subtle">
          Nothing is fabricated while its authoritative files cannot be read.
        </p>
      </Panel>
    );
  }
  if (lab.catalogueLoading || lab.catalogue === null) {
    return (
      <div aria-busy="true" className="px-5 py-6 text-sm text-text-subtle">
        Loading scenario catalogue…
      </div>
    );
  }
  return (
    <JourneyCatalogue
      view={lab.catalogue}
      selectedRunId={lab.selectedRunId}
      replayingScenarioId={lab.replayingScenarioId}
      onSelectRun={lab.selectRun}
      onReplay={(scenarioId) => void lab.replay(scenarioId)}
      liveControl={live.status?.control ?? null}
      liveOperation={live.operation}
      onRunLive={(scenarioId) => {
        void live.start(scenarioId).then((receipt) => {
          if (receipt !== undefined) lab.selectStartedRun(receipt.runId);
        });
      }}
    />
  );
}

function RunPane({ lab }: { readonly lab: JourneyLabController }): React.JSX.Element {
  if (lab.runError) {
    return (
      <Panel className="p-5">
        <p className="text-sm text-text-muted">This run’s evidence is unavailable.</p>
        <p className="mt-1 text-sm text-text-subtle">
          The catalogue remains usable, but no run detail is invented.
        </p>
      </Panel>
    );
  }
  if (lab.runLoading) {
    return (
      <div aria-busy="true" className="px-5 py-6 text-sm text-text-subtle">
        Loading run evidence…
      </div>
    );
  }
  if (lab.selectedRun !== null) {
    return (
      <JourneyRunDetail
        run={lab.selectedRun}
        replaying={lab.replayingScenarioId === lab.selectedRun.scenarioId}
        onReplay={(scenarioId) => void lab.replay(scenarioId)}
      />
    );
  }
  return (
    <Panel className="p-5">
      <p className="text-sm text-text-muted">Select a recorded run or start a scripted check.</p>
      <p className="mt-1 text-sm text-text-subtle">
        Its step, assertion and evidence timeline will appear here.
      </p>
    </Panel>
  );
}

export function JourneyLabSurface({
  api,
  liveApi,
}: JourneyLabSurfaceProps = {}): React.JSX.Element {
  const transport = useMemo(() => api ?? httpJourneyLabApi(), [api]);
  const liveTransport = useMemo(() => liveApi ?? httpJourneyLiveApi(), [liveApi]);
  const lab = useJourneyLab(transport);
  const live = useJourneyLive(liveTransport);
  return (
    <>
      <ScreenToolbar title="Journey Lab">
        {lab.catalogue !== null ? (
          <TbStamp>{lab.catalogue.journeys.length} scenarios</TbStamp>
        ) : null}
        <TbInfo
          title="Journey Lab"
          tip="Run the scenario pack, inspect each durable step and assertion, and see the plain proof boundary for the exact recorded execution mode."
        />
      </ScreenToolbar>
      <ScreenBody>
        <div className="space-y-6" data-journey-lab>
          <JourneyLiveControl live={live} />
          <ScriptedRailPanel />
          {lab.replayError ? (
            <div role="alert" className="panel border-clay p-4 text-sm text-text-muted">
              The replay request did not return a confirmed outcome. The catalogue has been
              refreshed so any durably queued run remains visible.
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div>
              <CataloguePane lab={lab} live={live} />
            </div>
            <div>
              <RunPane lab={lab} />
            </div>
          </div>
        </div>
      </ScreenBody>
    </>
  );
}
